import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Divider, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Images } from "../../../utils/Images";
import { getVendorShareDetails, getVendors } from "../../../api/services/vendorService";
import { canViewPrivateCampaign } from "../../../utils/authUtils";
import { formatCompactInr } from "../../../utils/formatCompactInr";

const formatCurrency = (value) => formatCompactInr(value);

const sanitizeUrl = (url) => String(url || "").replace(/%22$/i, "").replace(/"+$/, "");
const toFlag = (value) => Number(value) === 1;

const FeaturedVendorsSection = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const home = useSelector((state) => state.cms.home);
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const canAccessPrivateVendor = canViewPrivateCampaign(user, isAuthenticated);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  const isDesktopGrid = useMediaQuery("(min-width: 1390px)");
  const [vendors, setVendors] = useState([]);
  const [shareByVendorId, setShareByVendorId] = useState({});

  const sections = Array.isArray(home?.data) ? home.data : [];
  const featuredVendorsCms = sections.find((item) => {
    if (!item) return false;
    const isActive = item.status === 1 || item.status === true;
    if (!isActive) return false;
    const isFeaturedVendors = String(item.title || "").toLowerCase().includes("featured vendors");
    return isFeaturedVendors;
  });

  const sectionTitle = featuredVendorsCms?.sub_title || "Meet The Vendors Behind Every Product";
  const sectionDescription =
    featuredVendorsCms?.description ||
    "Every vendor has a story. Buy their products and we'll distribute them to partner NGOs - empowering the vendor and supporting the community.";

  useEffect(() => {
    let mounted = true;
    const fetchVendors = async () => {
      try {
        const response = await getVendors();
        const payload = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
        if (!mounted) return;
        setVendors(payload);
      } catch (error) {
        if (!mounted) return;
        setVendors([]);
      }
    };
    fetchVendors();
    return () => {
      mounted = false;
    };
  }, []);

  const featuredVendorsData = useMemo(() => {
    const activeVendors = vendors
      .filter((item) => {
        if (!item || !(item.status === 1 || item.status === true)) return false;
        const isPrivate = toFlag(item.is_private);
        if (!isPrivate) return true;
        return canAccessPrivateVendor;
      })
      .map((item) => ({
        id: item.id,
        title: item.title || "",
        slug:
          (Array.isArray(item.redirects) &&
            (item.redirects.find((r) => r?.primary_url)?.primary_url ||
              item.redirects.find((r) => r?.secondary_url && !r?.secondaryUrlHide)?.secondary_url)) ||
          "",
        location: item.vendor_location || "",
        locationLink: sanitizeUrl(item.location_link),
        tags: Array.isArray(item.category_tags) ? item.category_tags.filter(Boolean) : [],
        goalAmountRaw: Number(item.goal_amount || 0),
        raisedAmountRaw: Number(item.raised_amount || 0),
        progressPercentage: item.progress_percentage,
        goal: formatCurrency(item.goal_amount),
        raised: formatCurrency(item.raised_amount),
        supporters: String(item.supporters ?? item.supporters_count ?? 0),
        image: sanitizeUrl(item.thumbnail_image) || Images.fallBackImageCard,
        badge: item.tag_field || "Feeds 50+ Children",
        hideGoal: toFlag(item.hide_goal),
        hideRaised: toFlag(item.hide_raised),
        hideAll: toFlag(item.hide_all),
        closedVendor: toFlag(item.closed_vendor),
        holdVendor: toFlag(item.hold_vendor),
        isPrivate: toFlag(item.is_private),
        socialHandles: {
          facebook: Array.isArray(item.socialMediaLinks) ? item.socialMediaLinks.some((s) => s?.facebook === true) : false,
          linkedin: Array.isArray(item.socialMediaLinks) ? item.socialMediaLinks.some((s) => s?.linkedin === true) : false,
          twitter: Array.isArray(item.socialMediaLinks) ? item.socialMediaLinks.some((s) => s?.twitter === true) : false,
          whatsapp: Array.isArray(item.socialMediaLinks) ? item.socialMediaLinks.some((s) => s?.whatsapp === true) : false,
        },
      }));
    return activeVendors;
  }, [vendors, canAccessPrivateVendor]);

  useEffect(() => {
    let mounted = true;
    const activeVendorIds = vendors.filter((item) => item && (item.status === 1 || item.status === true) && item.id).map((item) => item.id);
    if (activeVendorIds.length === 0) {
      setShareByVendorId({});
      return;
    }

    const fetchShareData = async () => {
      try {
        const results = await Promise.all(
          activeVendorIds.map(async (vendorId) => {
            try {
              const response = await getVendorShareDetails(vendorId);
              const payload = response?.data?.data || response?.data || response;
              return [vendorId, payload || null];
            } catch (error) {
              return [vendorId, null];
            }
          })
        );
        if (!mounted) return;
        setShareByVendorId(
          results.reduce((acc, [vendorId, data]) => {
            acc[vendorId] = data;
            return acc;
          }, {})
        );
      } catch (error) {
        if (!mounted) return;
        setShareByVendorId({});
      }
    };

    fetchShareData();
    return () => {
      mounted = false;
    };
  }, [vendors]);

  const styles = isMobile
    ? {
        container: {
          width: "100%",
          borderRadius: "16px",
          paddingTop: "24px",
          paddingBottom: "24px",
          paddingLeft: "16px",
          paddingRight: "16px",
        },
        sectionHeader: { fontSize: "10px", letterSpacing: "2px", marginBottom: "10px" },
        title: { fontSize: "18px", lineHeight: "29.124px", marginBottom: "10px" },
        description: { fontSize: "12px", lineHeight: "20px" },
      }
    : isTablet
      ? {
          container: {
            width: "94.44%",
            borderRadius: "24px",
            paddingTop: "30px",
            paddingBottom: "30px",
            paddingLeft: "24px",
            paddingRight: "24px",
          },
          sectionHeader: { fontSize: "12px", letterSpacing: "2px", marginBottom: "12px" },
          title: { fontSize: "40px", lineHeight: "120%", marginBottom: "12px" },
          description: { fontSize: "14px", lineHeight: "22px" },
        }
      : {
          container: {
            width: "94.44%",
            borderRadius: "36px",
            paddingTop: "35px",
            paddingBottom: "35px",
            paddingLeft: "30px",
            paddingRight: "30px",
          },
          sectionHeader: { fontSize: "14px", letterSpacing: "2px", marginBottom: "12px" },
          title: { fontSize: "36px", lineHeight: "120%", marginBottom: "12px" },
          description: { fontSize: "16px", lineHeight: "24px" },
        };

  return (
    <Box
      id="featured-vendors-section"
      sx={{
        ...styles.container,
        maxWidth: "1440px",
        background: "#F7F7F7",
        margin: "0 auto",
        marginBottom: isMobile ? "30px" : "60px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: styles.sectionHeader.marginBottom }}>
        <Box
          sx={{
            width: isMobile ? "6px" : "8px",
            height: isMobile ? "6px" : "8px",
            borderRadius: "50%",
            backgroundColor: "#8EA946",
          }}
        />
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: styles.sectionHeader.fontSize,
            lineHeight: 1,
            letterSpacing: styles.sectionHeader.letterSpacing,
            textTransform: "uppercase",
            color: "#1D1D1B",
          }}
        >
          Featured Vendors
        </Typography>
      </Box>

      <Typography
        sx={{
          fontWeight: 700,
          fontSize: styles.title.fontSize,
          lineHeight: styles.title.lineHeight,
          letterSpacing: isMobile ? "-0.36px" : "normal",
          color: "#1D1D1B",
          marginBottom: styles.title.marginBottom,
          maxWidth: "none",
        }}
      >
        {sectionTitle}
      </Typography>

      <Typography
        sx={{
          fontWeight: 400,
          fontSize: styles.description.fontSize,
          lineHeight: styles.description.lineHeight,
          letterSpacing: isMobile ? "-0.24px" : "normal",
          color: "#686866",
          maxWidth: isMobile ? "none" : "1161px",
        }}
      >
        {sectionDescription}
      </Typography>

      <Box
        className="hide-scrollbar"
        sx={{
          width: isMobile ? "calc(100% + 32px)" : "100%",
          ml: isMobile ? "-16px" : 0,
          mr: isMobile ? "-16px" : 0,
          pl: isMobile ? "16px" : 0,
          mt: isMobile ? "20px" : "24px",
          display: isDesktopGrid ? "grid" : "flex",
          gridTemplateColumns: isDesktopGrid ? "repeat(3, 417px)" : "none",
          justifyContent: isDesktopGrid ? "center" : "flex-start",
          gap: isMobile ? "16px" : "20px",
          overflowX: isDesktopGrid ? "hidden" : "auto",
          overflowY: "hidden",
          pb: isDesktopGrid ? 0 : "4px",
          pr: isMobile ? "16px" : 0,
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {featuredVendorsData.map((vendor) => (
          (() => {
            const goalAmount = Number(vendor.goalAmountRaw || 0);
            const raisedAmount = Number(vendor.raisedAmountRaw || 0);
            // Backend sends progressPercentage computed before hide_* flags null the raw amounts,
            // so the bar stays accurate even when those figures aren't in the payload.
            const progress =
              vendor.progressPercentage != null
                ? Number(vendor.progressPercentage)
                : goalAmount > 0
                  ? Math.min(Math.round((raisedAmount / goalAmount) * 100), 100)
                  : 0;
            const shareDetails = shareByVendorId[vendor.id];
            const shareHandles = shareDetails?.social_handles || vendor.socialHandles || {};
            const shareLinks = shareDetails?.share_links || {};
            const shareIcons = [
              { key: "facebook", icon: Images.facebook },
              { key: "linkedin", icon: Images.linkedin },
              { key: "twitter", icon: Images.twitter },
              { key: "whatsapp", icon: Images.whatsapp },
            ];
            const visibleShareIcons = shareIcons.filter((item) => shareHandles?.[item.key] === true);
            const isUnavailableVendor = vendor.closedVendor || (vendor.isPrivate && !canAccessPrivateVendor);

            return (
          <Box
            key={vendor.id}
            onClick={() => navigate(vendor.slug ? `/small-vendors/${vendor.slug}` : `/small-vendors/detail/${vendor.id}`)}
            sx={{
              width: isDesktopGrid ? "417px" : isMobile ? "304px" : "400px",
              minWidth: isDesktopGrid ? "417px" : isMobile ? "304px" : "400px",
              background: "#FFFFFF",
              borderRadius: isMobile ? "10px" : "16px",
              boxShadow: isMobile ? "0px 7.194px 18.705px rgba(0,0,0,0.04)" : "0px 10px 26px rgba(0,0,0,0.04)",
              p: isMobile ? "14px" : "12px",
              display: "flex",
              flexDirection: "column",
              cursor: "pointer",
            }}
          >
            <Box
              sx={{
                height: isMobile ? "155px" : "217px",
                borderRadius: isMobile ? "15.984px" : "16px",
                position: "relative",
                overflow: "hidden",
                mb: "12px",
              }}
            >
              <Box
                component="img"
                src={vendor.image}
                alt={vendor.title}
                onError={(e) => {
                  e.currentTarget.src = Images.fallBackImageCard;
                }}
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              <Box
                sx={{
                  position: "absolute",
                  right: "10px",
                  bottom: "10px",
                  px: isMobile ? "12px" : "12px",
                  py: isMobile ? "6px" : "6px",
                  borderRadius: "4px",
                  background: "#A9C74E",
                  color: "#fff",
                  fontSize: isMobile ? "10px" : "12px",
                  fontWeight: 700,
                  lineHeight: "16px",
                  letterSpacing: isMobile ? "-0.2px" : "normal",
                }}
              >
                {vendor.badge || "Feeds 50+ Children"}
              </Box>
            </Box>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: isMobile ? "14px" : "18px",
                lineHeight: isMobile ? "22.652px" : "29px",
                color: "#1D1D1B",
                mb: "8px",
                minHeight: isMobile ? "66px" : "58px",
              }}
            >
              {vendor.title}
            </Typography>

            <Box
              onClick={(e) => {
                if (!vendor.locationLink) return;
                e.stopPropagation();
                window.open(vendor.locationLink, "_blank", "noopener,noreferrer");
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                mb: "12px",
                cursor: vendor.locationLink ? "pointer" : "default",
              }}
            >
              <Box component="img" src={Images.gps} alt="location" sx={{ width: isMobile ? "18px" : "16px", height: isMobile ? "18px" : "16px", opacity: 0.8 }} />
              <Typography sx={{ fontWeight: 500, fontSize: isMobile ? "12px" : "14px", lineHeight: isMobile ? 1 : "18px", color: "#686866" }}>
                {vendor.location}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap", mb: "12px" }}>
              {vendor.tags.map((tag) => (
                <Box
                  key={tag}
                  sx={{
                    height: isMobile ? "26px" : "auto",
                    px: "12px",
                    py: isMobile ? "6px" : "6px",
                    borderRadius: isMobile ? "6px" : "8px",
                    border: "1px solid #D1DFAD",
                    background: "#F5F8ED",
                    color: "#8EA946",
                    fontWeight: 600,
                    fontSize: isMobile ? "10px" : "11px",
                    lineHeight: isMobile ? 1 : "18px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {tag}
                </Box>
              ))}
            </Box>

            <Box>
              {/* Progress bar always shown, even when "Hide All" is on — only the goal/raised/supporters row below is gated by hideAll */}
              <Box sx={{ position: "relative", height: isMobile ? "16px" : "24px", mb: "12px" }}>
                <Box sx={{ position: "absolute", left: 0, right: 0, top: isMobile ? "4px" : "8px", height: isMobile ? "8.633px" : "8px", borderRadius: "20px", background: "#E8E8E8", opacity: 0.6 }} />
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: isMobile ? "6px" : "9px",
                    width: progress >= 100 ? "100%" : `${progress}%`,
                    maxWidth: "100%",
                    height: isMobile ? "4.317px" : "6px",
                    borderRadius: isMobile ? "2.878px" : "4px",
                    background: "linear-gradient(90deg,#9CBA4D 0%, #9AB453 100%)",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left:
                      progress <= 8
                        ? "0px"
                        : progress >= 95
                          ? "calc(100% - 44px)"
                          : `calc(${progress}% - 20px)`,
                    px: isMobile ? "5.755px" : "8px",
                    py: isMobile ? "1.439px" : "2px",
                    borderRadius: isMobile ? "2.878px" : "4px",
                    background: "#8EA946",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: isMobile ? "10.072px" : "14px",
                    lineHeight: isMobile ? "12.95px" : "18px",
                  }}
                >
                  {progress}%
                </Box>
              </Box>

              {!vendor.hideAll && (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "14px" }}>
                  {!vendor.hideGoal && !vendor.hideRaised && (
                    <>
                      {[
                        { key: "Goal", value: vendor.goal, icon: Images.rupee },
                        { key: "Raised", value: vendor.raised, icon: Images.rupee },
                      ].map((item) => (
                        <React.Fragment key={item.key}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? "6px" : "8px", flex: 1 }}>
                            <Box sx={{ width: isMobile ? "24px" : "32px", height: isMobile ? "24px" : "32px", borderRadius: isMobile ? "5.755px" : "8px", border: "1px solid #D1DFAD", background: "#F5F8ED", display: "grid", placeItems: "center" }}>
                              <Box component="img" src={item.icon} alt={item.key} sx={{ width: isMobile ? "14px" : "18px", height: isMobile ? "14px" : "18px" }} />
                            </Box>
                            <Box>
                              <Typography sx={{ fontWeight: item.key === "Raised" ? 700 : 500, fontSize: isMobile ? "10px" : "12px", lineHeight: isMobile ? "12.95px" : "16px", color: "#686866" }}>{item.key}</Typography>
                              <Typography sx={{ fontWeight: item.key === "Raised" ? 800 : 700, fontSize: isMobile ? "12px" : "14px", lineHeight: isMobile ? "12.95px" : "18px", letterSpacing: isMobile ? "-0.24px" : "normal", color: "#1D1D1B" }}>{item.value}</Typography>
                            </Box>
                          </Box>
                          <Divider orientation="vertical" flexItem sx={{ mx: isMobile ? "6px" : "8px" }} />
                        </React.Fragment>
                      ))}
                    </>
                  )}
                  {vendor.hideGoal && !vendor.hideRaised && (
                    <>
                      <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? "6px" : "8px", flex: 1 }}>
                        <Box sx={{ width: isMobile ? "24px" : "32px", height: isMobile ? "24px" : "32px", borderRadius: isMobile ? "5.755px" : "8px", border: "1px solid #D1DFAD", background: "#F5F8ED", display: "grid", placeItems: "center" }}>
                          <Box component="img" src={Images.rupee} alt="Raised" sx={{ width: isMobile ? "14px" : "18px", height: isMobile ? "14px" : "18px" }} />
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: isMobile ? "10px" : "12px", lineHeight: isMobile ? "12.95px" : "16px", color: "#686866" }}>Raised</Typography>
                          <Typography sx={{ fontWeight: 800, fontSize: isMobile ? "12px" : "14px", lineHeight: isMobile ? "12.95px" : "18px", letterSpacing: isMobile ? "-0.24px" : "normal", color: "#1D1D1B" }}>{vendor.raised}</Typography>
                        </Box>
                      </Box>
                      <Divider orientation="vertical" flexItem sx={{ mx: isMobile ? "6px" : "8px" }} />
                    </>
                  )}
                  <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? "6px" : "8px", flex: 1 }}>
                    <Box sx={{ width: isMobile ? "24px" : "32px", height: isMobile ? "24px" : "32px", borderRadius: isMobile ? "5.755px" : "8px", border: "1px solid #D1DFAD", background: "#F5F8ED", display: "grid", placeItems: "center" }}>
                      <Box component="img" src={Images.people} alt="Supporters" sx={{ width: isMobile ? "14px" : "18px", height: isMobile ? "14px" : "18px" }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 500, fontSize: isMobile ? "10px" : "12px", lineHeight: isMobile ? "12.95px" : "16px", color: "#686866" }}>Supporters</Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: isMobile ? "12px" : "14px", lineHeight: isMobile ? "12.95px" : "18px", letterSpacing: isMobile ? "-0.24px" : "normal", color: "#1D1D1B" }}>{vendor.supporters}</Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            <Divider sx={{ mb: "12px" }} />

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", gap: "8px" }}>
                {visibleShareIcons.map((item) => (
                  <Box
                    key={item.key}
                    onClick={(e) => {
                      e.stopPropagation();
                      const shareUrl = shareLinks?.[item.key];
                      if (!shareUrl) return;
                      window.open(shareUrl, "_blank", "noopener,noreferrer");
                    }}
                    sx={{
                      width: isMobile ? "28.777px" : "40px",
                      height: isMobile ? "28.777px" : "40px",
                      border: "1px solid #E8E8E8",
                      borderRadius: isMobile ? "7.194px" : "10px",
                      display: "grid",
                      placeItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <Box component="img" src={item.icon} alt={item.key} sx={{ width: isMobile ? "17.266px" : "24px", height: isMobile ? "17.266px" : "24px" }} />
                  </Box>
                ))}
              </Box>

              <Button
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isUnavailableVendor) return;
                  navigate(vendor.slug ? `/small-vendors/${vendor.slug}` : `/small-vendors/detail/${vendor.id}`);
                }}
                disabled={isUnavailableVendor}
                sx={{
                  width: isMobile ? "115.108px" : "148px",
                  height: isMobile ? "28.777px" : "40px",
                  borderRadius: isMobile ? "7.194px" : "9px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: isMobile ? "10px" : "14px",
                  lineHeight: isMobile ? "10.072px" : "normal",
                  background: "#F04951",
                  boxShadow: isMobile ? "0px 2.878px 0px #141414" : "0px 3.7px 0px #141414",
                  "&:hover": { background: "#F04951" },
                  "&.Mui-disabled": {
                    color: "#FFFFFF",
                    opacity: 0.65,
                    background: "#F04951",
                  },
                }}
              >
                {vendor.closedVendor ? "Vendor Closed" : "Buy Now"}
              </Button>
            </Box>
          </Box>
            );
          })()
        ))}
      </Box>
    </Box>
  );
};

export default FeaturedVendorsSection;
